import {
  BufferAttribute,
  BufferGeometry,
  Line,
  LineBasicMaterial,
  PerspectiveCamera,
  Raycaster,
  Scene,
  Vector2,
  Vector3,
} from 'three';
import { WEB_SIDES, WebSide } from '../core/types';
import { WEB } from '../config/settings';
import { Input } from '../core/Input';
import { AudioEngine } from '../core/Audio';
import { BuildingRegistry } from '../world/BuildingRegistry';
import { Player } from './Player';
import { HandsRig } from './HandsRig';

interface Web {
  active: boolean;
  readonly anchor: Vector3;
  length: number;
  readonly line: Line;
  readonly linePositions: BufferAttribute;
}

export type WebStateListener = (side: WebSide, active: boolean) => void;

/**
 * Manages the two webs: firing (raycast against buildings), the pendulum
 * constraint that produces the swing, reeling in, and rendering the web lines.
 * It drives the HandsRig and exposes `applyConstraints` for the movement
 * controller to call after integration.
 */
export class WebSystem {
  private readonly raycaster = new Raycaster();
  private readonly screenCentre = new Vector2(0, 0);
  private readonly webs: Record<WebSide, Web>;

  // scratch
  private readonly eye = new Vector3();
  private readonly delta = new Vector3();
  private readonly normal = new Vector3();
  private readonly fistWorld = new Vector3();

  private listener: WebStateListener = () => {};

  constructor(
    scene: Scene,
    private readonly registry: BuildingRegistry,
    private readonly camera: PerspectiveCamera,
    private readonly hands: HandsRig,
    private readonly input: Input,
    private readonly audio: AudioEngine,
    private readonly player: Player,
  ) {
    this.raycaster.far = WEB.MAX_LENGTH;
    this.webs = { L: this.createWeb(scene), R: this.createWeb(scene) };
  }

  onStateChange(listener: WebStateListener): void {
    this.listener = listener;
  }

  get isSwinging(): boolean {
    return this.webs.L.active || this.webs.R.active;
  }

  shoot(side: WebSide): void {
    this.audio.thwip();
    this.raycaster.setFromCamera(this.screenCentre, this.camera);
    const hit = this.raycaster.intersectObjects(this.registry.meshes, false)[0];
    const web = this.webs[side];

    if (!hit) {
      this.hands.beginShoot(side, null);
      return;
    }
    web.anchor.copy(hit.point);
    const distance = this.player.eyePosition(this.eye).distanceTo(web.anchor);
    web.length = Math.max(WEB.MIN_LENGTH, distance * WEB.ATTACH_SLACK);
    web.active = true;
    web.line.visible = true;
    this.hands.beginShoot(side, web.anchor);
    this.listener(side, true);
  }

  release(side: WebSide): void {
    const web = this.webs[side];
    web.active = false;
    web.line.visible = false;
    this.hands.endShoot(side);
    this.listener(side, false);
  }

  /** Pendulum constraint: pull the player back to rope length and remove the
   *  outward radial velocity, preserving the tangential swing. Run after the
   *  player has been integrated. */
  applyConstraints(dt: number): void {
    if (!this.isSwinging) return;
    const reeling = this.input.isDown('ShiftLeft') || this.input.isDown('ShiftRight');

    for (let iter = 0; iter < WEB.CONSTRAINT_ITERATIONS; iter++) {
      for (const side of WEB_SIDES) {
        const web = this.webs[side];
        if (!web.active) continue;
        if (reeling && iter === 0) web.length = Math.max(WEB.MIN_LENGTH, web.length - WEB.REEL_SPEED * dt);

        this.player.eyePosition(this.eye);
        this.delta.copy(this.eye).sub(web.anchor);
        const dist = this.delta.length();
        if (dist <= web.length) continue;

        this.normal.copy(this.delta).multiplyScalar(1 / dist);
        this.player.position.addScaledVector(this.normal, -(dist - web.length));
        const radial = this.player.velocity.dot(this.normal);
        if (radial > 0) this.player.velocity.addScaledVector(this.normal, -radial);
      }
    }
  }

  /** Animate the hands and redraw the web lines from fist to anchor. */
  update(dt: number): void {
    this.hands.update(dt);
    for (const side of WEB_SIDES) {
      const web = this.webs[side];
      if (!web.active) continue;
      this.hands.fistWorldPosition(side, this.fistWorld);
      const p = web.linePositions;
      p.setXYZ(0, this.fistWorld.x, this.fistWorld.y, this.fistWorld.z);
      p.setXYZ(1, web.anchor.x, web.anchor.y, web.anchor.z);
      p.needsUpdate = true;
    }
  }

  private createWeb(scene: Scene): Web {
    const geometry = new BufferGeometry();
    const positions = new BufferAttribute(new Float32Array(6), 3);
    geometry.setAttribute('position', positions);
    const line = new Line(geometry, new LineBasicMaterial({ color: 0xffffff }));
    line.frustumCulled = false;
    line.visible = false;
    scene.add(line);
    return { active: false, anchor: new Vector3(), length: 0, line, linePositions: positions };
  }
}
